import React, { useEffect, useState } from "react";
import { IconButton, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import "./WorkoutDisplay.css";
import { Typography, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { Icon } from "@iconify/react";
import MySlider from "./component/slider";
import { GymTVProgramsListingApi } from "src/DAL/GymTv/gymTv";
import ModalToSetprogram from "./component/ModalToSetprogram";
import RecordNotFound from "src/components/RecordNotFound";
import CustomPopover from "../UserInformation/CustomPopover";
import { useQuery } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));
const WorkoutDisplay = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [openProgramSelecter, setProgramSelecter] = useState(false);
  const [isPaused, setIsPaused] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isOpenNote, setIsOpenNote] = useState(false);
  const [inputs, setInputs] = useState({
    programs: [],
    programs_id: null,
    workout_of_day: null,
  });
  const [selectedWorkout, setSelectedWorkout] = useState({
    programs: [],
    programs_id: null,
    workout_of_day: null,
  });

  const toggleFullScreen = () => {
    if (!isFullScreen) {
      document.documentElement.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  const navigate = useNavigate();
  const [zoomLevel, setZoomLevel] = useState(100);
  const handleZoomIn = () => {
    setZoomLevel((prevZoom) => prevZoom + 10);
  };

  const handleZoomOut = () => {
    setZoomLevel((prevZoom) => Math.max(10, prevZoom - 10));
  };
  const handlePause = () => {
    setIsPaused(true);
  };

  const handlePlay = () => {
    setIsPaused(false);
  };

  const { data = [], isLoading } = useQuery({
    queryKey: ["program_workouts", "gym_tv"],
    keepPreviousData: true,
    queryFn: async () => {
      const result = await GymTVProgramsListingApi();
      if (result.code === 200) {
        return result?.program_workouts || [];
      }
      throw new Error(result.message || "Failed to load workouts");
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  useEffect(() => {
    if (!data?.length) return;

    const currentProgramId = selectedWorkout?.programs_id?._id;
    const currentWorkoutId = selectedWorkout?.workout_of_day?._id;

    const matchedProgram =
      data.find((program) => program?._id === currentProgramId) || data[0];
    const matchedWorkout = currentWorkoutId
      ? matchedProgram?.workouts?.find(
          (workout) => workout?._id === currentWorkoutId,
        )
      : null;

    const nextWorkout = matchedWorkout || matchedProgram?.workouts?.[0] || null;

    const programData = {
      programs: data,
      programs_id: matchedProgram,
      workout_of_day: nextWorkout,
    };

    setInputs((values) => ({
      ...values,
      ...programData,
    }));

    setSelectedWorkout((values) => ({
      ...values,
      ...programData,
    }));
  }, [
    data,
    selectedWorkout?.programs_id?._id,
    selectedWorkout?.workout_of_day?._id,
  ]);

  useEffect(() => {
    openProgramSelecter && setInputs(selectedWorkout);
  }, [openProgramSelecter]);

  if (isLoading === true) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return data.length > 0 ? (
    <div className="container">
      <ModalToSetprogram
        open={openProgramSelecter}
        setOpen={setProgramSelecter}
        inputs={inputs}
        setInputs={setInputs}
        setSelectedWorkout={setSelectedWorkout}
      />
      <div className="row mt-4 align-items-center justify-content-between">
        {isPaused ? (
          <>
            <div className="d-flex align-items-center justify-content-start col-12 col-md-9">
              <IconButton
                className="back-screen-button"
                onClick={() => navigate(-1)}
              >
                <ArrowBackIcon />
              </IconButton>
              <h2
                onClick={() => {
                  setProgramSelecter(true);
                }}
                style={{ cursor: "pointer" }}
              >
                {selectedWorkout?.programs_id?.title} -{" "}
                {selectedWorkout?.workout_of_day?.title}
              </h2>
              <Tooltip title="Change workout of the day">
                <div
                  onClick={() => {
                    setProgramSelecter(true);
                  }}
                >
                  <Icon
                    style={{ cursor: "pointer" }}
                    className="ms-2 mb-1"
                    onClick={() => {
                      setProgramSelecter(true);
                    }}
                    height={"35"}
                    width={"35"}
                    icon="bxs:edit"
                  />
                </div>
              </Tooltip>
            </div>
            <div className="col-12 col-md-3 text-end">
              <button
                onClick={toggleFullScreen}
                className="small-contained-button"
              >
                <Icon
                  icon={isFullScreen ? "mdi:fullscreen-exit" : "mdi:fullscreen"}
                  width={24}
                  height={24}
                />
              </button>
              {selectedWorkout?.workout_of_day?.exercises.length > 6 && (
                <button
                  onClick={handlePlay}
                  className="small-contained-button ms-3"
                >
                  <Icon icon="solar:play-bold" width={24} height={24} />
                </button>
              )}
            </div>
          </>
        ) : (
          <div>
            <div className="col-12 mt-2 text-end">
              <button
                onClick={toggleFullScreen}
                className="small-contained-button"
              >
                <Icon
                  icon={isFullScreen ? "mdi:fullscreen-exit" : "mdi:fullscreen"}
                  width={24}
                  height={24}
                />
              </button>
              <button
                onClick={handlePause}
                className="small-contained-button ms-3"
              >
                <Icon icon="mingcute:pause-fill" width={24} height={24} />
              </button>
            </div>
          </div>
        )}
        {selectedWorkout?.workout_of_day?.coach_notes && (
          <div className="col-12 text-end mt-3">
            <button
              onClick={() => {
                setIsOpenNote(true);
              }}
              className="small-contained-button"
            >
              Coach Note
            </button>
          </div>
        )}
      </div>

      <div style={{ zoom: `${zoomLevel}%` }}>
        <MySlider
          elements={
            selectedWorkout?.workout_of_day?.exercises
              ? selectedWorkout?.workout_of_day?.exercises
              : []
          }
          isPaused={isPaused}
          isFullScreen={isFullScreen}
          setIsFullScreen={setIsFullScreen}
          toggleFullScreen={toggleFullScreen}
        />
      </div>
      <div className="zoom-controls">
        <div className="me-2 cross-icon ">
          <RemoveCircleIcon
            onClick={handleZoomOut}
            className="diary_icon_plus_minus"
          />
        </div>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          Zoom
        </Typography>
        <div className=" cross-icon ms-2">
          <AddCircleIcon
            onClick={handleZoomIn}
            className="diary_icon_plus_minus"
          />
        </div>
      </div>
      <CustomPopover
        isOpenPop={isOpenNote}
        isClosePop={setIsOpenNote}
        title="Workout Note"
        componentToPassDown={
          <div className="p-4">
            <div
              dangerouslySetInnerHTML={{
                __html: selectedWorkout?.workout_of_day?.coach_notes,
              }}
            ></div>
          </div>
        }
      />
    </div>
  ) : (
    <div className="container">
      <IconButton className="back-screen-button " onClick={() => navigate(-1)}>
        <ArrowBackIcon />
      </IconButton>
      <RecordNotFound title="Today Workout" />
    </div>
  );
};

export default WorkoutDisplay;
